import debounce from "lodash/debounce";
import { useEffect, useMemo, useState } from "react";

/**
 * Hook to debounce channel ROI insight parameters
 */
export const useDebouncedInsights = ({
  startDate,
  endDate,
  selectedNodeUUID,
  funnelStage,
  channels,
  geographies,
  selectedNode,
  debounceMs = 500,
}: {
  startDate?: string | null;
  endDate?: string | null;
  selectedNodeUUID: string | null;
  funnelStage?: string | null;
  channels?: string[];
  geographies?: string[];
  selectedNode: any | null;
  debounceMs?: number;
}) => {
  const [debouncedValues, setDebouncedValues] = useState({
    startDate,
    endDate,
    selectedNodeUUID,
    funnelStage: funnelStage ?? "all",
    channels: channels ?? [],
    geographies: geographies ?? [],
    brand: selectedNode?.node.brand ?? "",
    category: selectedNode?.node.category ?? "",
    product: selectedNode?.node.product ?? "",
  });

  // Update debounced value after delay
  useEffect(() => {
    const hasValidChannels = Array.isArray(channels) && channels.length > 0;
    if (!hasValidChannels) {
      setDebouncedValues((prev) => ({
        ...prev,
        channels: [],
      }));
      return;
    }

    const handler = debounce(() => {
      setDebouncedValues({
        startDate,
        endDate,
        selectedNodeUUID,
        funnelStage: funnelStage ?? "all",
        channels: channels ?? [],
        geographies: geographies ?? [],
        brand: selectedNode?.node.brand ?? "",
        category: selectedNode?.node.category ?? "",
        product: selectedNode?.node.product ?? "",
      });
    }, debounceMs);

    handler();

    return () => {
      handler.cancel();
    };
  }, [
    startDate,
    endDate,
    selectedNodeUUID,
    funnelStage,
    channels,
    geographies,
    selectedNode,
    debounceMs,
  ]);

  // Validate all parameters
  const isValid = useMemo(() => {
    return (
      debouncedValues.startDate &&
      debouncedValues.endDate &&
      debouncedValues.selectedNodeUUID &&
      debouncedValues.funnelStage &&
      !!debouncedValues.brand &&
      !!debouncedValues.category &&
      !!debouncedValues.product &&
      Array.isArray(debouncedValues.channels) &&
      debouncedValues.channels.length > 0
    );
  }, [debouncedValues]);

  return {
    debouncedParams: debouncedValues,
    isValid,
  };
};
